import { InjectionToken, Signal } from '@angular/core'

import { WorkspaceType } from '@flowx/designer/modules/workspaces/shared/commons.types'
import {
  ApplicationStatus,
  ApplicationType,
} from '@flowx/designer/shared/entities/application.entity'

import {
  BuildMigrationNotificationPayload,
  ExportNotificationPayload,
  NotificationSummary,
  buildExportSummary,
  buildImportSummary,
  buildMigrationSummary,
} from './notifications-payload.model'

/**
 * A notification exactly as designer-user sends it, both in the history response and inside an SSE
 * frame — the backend's own field names, so nothing is remapped on the way in. `type` decides what
 * `payload` holds; an import's is the backend's empty base DTO, so none is declared.
 */
export type UserNotificationEvent = NotificationEnvelope &
  (
    | { type: NotificationType.BUILD_MIGRATION; payload: BuildMigrationNotificationPayload }
    | { type: NotificationType.APP_VERSION_EXPORT; payload: ExportNotificationPayload }
    | { type: NotificationType.APP_VERSION_IMPORT }
    | { type: NotificationType.BUILD_EXPORT; payload: ExportNotificationPayload }
    | { type: NotificationType.BUILD_IMPORT }
  )

/** The fields every notification carries, whatever its type. */
interface NotificationEnvelope {
  id: string
  version: number
  status: NotificationStatus
  markedRead: boolean
  title: string
  body?: string
  /**
   * Epoch seconds with fractional nanos from one serializer, an ISO-8601 string from another;
   * `notificationTimestampMs` is the single place that resolves the two.
   */
  createdTimestamp: number | string
  orgId: string
  orgName?: string
  workspaceId: string
  workspaceName?: string
  workspaceType: WorkspaceType
  appId: string
  appName?: string
  appType?: ApplicationType
  appVersionId: string
  appVersionStatus?: ApplicationStatus
  branchId?: string
  branchName?: string
  branchOrder?: number
  buildId?: string
  correlationId?: string // Shared by every notification of one long-running action (mergeId, migrationId, …).
  hasDetails: boolean
  url?: string
}

/**
 * Sent on the same channel as the notifications themselves whenever the user reads one, wherever
 * they read it — so the tab that did the reading gets its own event back too. The backend sends it
 * as a notification DTO with only `id` filled in; every other field arrives null or defaulted
 * (`markedRead` reads `false`), so nothing but `id` is declared here.
 *
 * A null `id` is the mark-all, and means "everything you are holding". That is only a safe
 * instruction because both kinds of frame share one stream: a notification the server published
 * after the mark-all ran is ordered after this event, so it cannot already be in the list by the
 * time this arrives.
 */
export interface NotificationReadEvent {
  id: string | null
}

export enum NotificationType {
  BUILD_MIGRATION = 'BUILD_MIGRATION',
  APP_VERSION_EXPORT = 'APP_VERSION_EXPORT',
  APP_VERSION_IMPORT = 'APP_VERSION_IMPORT',
  BUILD_EXPORT = 'BUILD_EXPORT',
  BUILD_IMPORT = 'BUILD_IMPORT',
}

export enum NotificationStatus {
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
  FAILED = 'FAILED',
  IN_PROGRESS = 'IN_PROGRESS',
}

export const NOTIFICATION_DURATION = 5000

/** A toast that offers an action stays up longer, so there is time to reach for it. */
export const NOTIFICATION_WITH_ACTION_DURATION = 7000

export interface NotificationStatusVisuals {
  borderClass: string
  badgeClass: string
  iconClass: string
}

export const VISUALS_BY_STATUS: Record<NotificationStatus, NotificationStatusVisuals> = {
  [NotificationStatus.SUCCESS]: {
    borderClass: 'border-l-8 border-green-500',
    badgeClass: 'bg-green-50',
    iconClass: 'ph ph-check-circle text-green-500',
  },
  [NotificationStatus.WARNING]: {
    borderClass: 'border-l-8 border-orange-500',
    badgeClass: 'bg-orange-50',
    iconClass: 'ph ph-warning text-orange-500',
  },
  [NotificationStatus.FAILED]: {
    borderClass: 'border-l-8 border-red-500',
    badgeClass: 'bg-red-50',
    iconClass: 'ph ph-warning-circle text-red-500',
  },
  [NotificationStatus.IN_PROGRESS]: {
    borderClass: 'border-l-8 border-blue-500',
    badgeClass: 'bg-blue-50',
    iconClass: 'ph ph-clock text-blue-500',
  },
}

export const NOTIFICATIONS_DATA = new InjectionToken<NotificationsData>('NOTIFICATIONS_DATA')

export interface NotificationsData {
  notifications: Signal<ExtendedNotification[]>
  dismiss: (id: string) => void
  setPaused: (paused: boolean) => void
}

export const COUNT_NOUN_BY_TYPE: Partial<
  Record<NotificationType, Partial<Record<NotificationStatus, string>>>
> = {
  [NotificationType.BUILD_MIGRATION]: {
    [NotificationStatus.SUCCESS]: 'instances',
    [NotificationStatus.WARNING]: 'affected',
    [NotificationStatus.FAILED]: 'affected',
  },
}

export enum NotificationActionType {
  RETRY_EXPORT = 'RETRY_EXPORT',
  REVIEW_EXPORT = 'REVIEW_EXPORT',
  DOWNLOAD_EXPORT = 'DOWNLOAD_EXPORT',
  RETRY_BUILD_EXPORT = 'RETRY_BUILD_EXPORT',
  REVIEW_BUILD_EXPORT = 'REVIEW_BUILD_EXPORT',
  DOWNLOAD_BUILD_EXPORT = 'DOWNLOAD_BUILD_EXPORT',
  VIEW_IMPORT = 'VIEW_IMPORT',
  RETRY_IMPORT = 'RETRY_IMPORT',
  VIEW_BUILD_IMPORT = 'VIEW_BUILD_IMPORT',
  RETRY_BUILD_IMPORT = 'RETRY_BUILD_IMPORT',
}

/** What the notification offers to do about its outcome — the card's last row. */
export interface NotificationAction {
  type: NotificationActionType
  label: string
  iconClass: string
}

/** Runs one action. Each notification type's handlers come from its own factory in `actions/`. */
export type NotificationActionHandler = (notification: ExtendedNotification) => void

export const ACTION_BY_TYPE: Partial<
  Record<NotificationType, Partial<Record<NotificationStatus, NotificationAction>>>
> = {
  [NotificationType.APP_VERSION_EXPORT]: {
    [NotificationStatus.FAILED]: {
      type: NotificationActionType.RETRY_EXPORT,
      label: 'Retry',
      iconClass: 'ph ph-arrow-counter-clockwise',
    },
    [NotificationStatus.WARNING]: {
      type: NotificationActionType.REVIEW_EXPORT,
      label: 'Review',
      iconClass: 'ph ph-arrow-right',
    },
    [NotificationStatus.SUCCESS]: {
      type: NotificationActionType.DOWNLOAD_EXPORT,
      label: 'Download',
      iconClass: 'ph ph-download-simple',
    },
  },
  [NotificationType.BUILD_EXPORT]: {
    [NotificationStatus.FAILED]: {
      type: NotificationActionType.RETRY_BUILD_EXPORT,
      label: 'Retry',
      iconClass: 'ph ph-arrow-counter-clockwise',
    },
    [NotificationStatus.WARNING]: {
      type: NotificationActionType.REVIEW_BUILD_EXPORT,
      label: 'Review',
      iconClass: 'ph ph-arrow-right',
    },
    [NotificationStatus.SUCCESS]: {
      type: NotificationActionType.DOWNLOAD_BUILD_EXPORT,
      label: 'Download',
      iconClass: 'ph ph-download-simple',
    },
  },
  // A warning import still landed the version (stashed versions are a caveat, not a pending
  // decision), so it offers the same View as a clean one.
  [NotificationType.APP_VERSION_IMPORT]: {
    [NotificationStatus.FAILED]: {
      type: NotificationActionType.RETRY_IMPORT,
      label: 'Retry',
      iconClass: 'ph ph-arrow-counter-clockwise',
    },
    [NotificationStatus.WARNING]: {
      type: NotificationActionType.VIEW_IMPORT,
      label: 'View',
      iconClass: 'ph ph-arrow-right',
    },
    [NotificationStatus.SUCCESS]: {
      type: NotificationActionType.VIEW_IMPORT,
      label: 'View',
      iconClass: 'ph ph-arrow-right',
    },
  },
  // The backend sends no warning for a build import.
  [NotificationType.BUILD_IMPORT]: {
    [NotificationStatus.FAILED]: {
      type: NotificationActionType.RETRY_BUILD_IMPORT,
      label: 'Retry',
      iconClass: 'ph ph-arrow-counter-clockwise',
    },
    [NotificationStatus.SUCCESS]: {
      type: NotificationActionType.VIEW_BUILD_IMPORT,
      label: 'View',
      iconClass: 'ph ph-arrow-right',
    },
  },
}

/**
 * A notification's sections, top to bottom: `title` and `body` are the backend's text; the context
 * line (workspace type, workspace, project or library), the `summary` and the `action` are built
 * here.
 */
export type ExtendedNotification = UserNotificationEvent & {
  summary: NotificationSummary
  action?: NotificationAction
  searchFields: string[]
  /**
   * designer-user names the workspace and the project from one lookup, which finds nothing while an
   * import is still creating the project, so an in-progress import can arrive with none of them.
   */
  hasContext: boolean
}

/** A version or a build export: both carry what the export was run with, so they share one review. */
export type ExportNotification = Extract<
  ExtendedNotification,
  { type: NotificationType.APP_VERSION_EXPORT | NotificationType.BUILD_EXPORT }
>

export const extendNotification = (
  notificationEvent: UserNotificationEvent
): ExtendedNotification => {
  const summary = buildNotificationSummary(notificationEvent)

  return {
    ...notificationEvent,
    summary,
    action: ACTION_BY_TYPE[notificationEvent.type]?.[notificationEvent.status],
    searchFields: buildNotificationSearchFields(notificationEvent, summary),
    hasContext: Boolean(
      notificationEvent.workspaceType &&
      notificationEvent.workspaceName &&
      notificationEvent.appName
    ),
  }
}

// Kept here rather than beside the builders: the count noun is looked up by this file's enums, and
// the payload file must not import them (see the note at its top).
const buildNotificationSummary = (
  notificationEvent: UserNotificationEvent
): NotificationSummary => {
  const { type, status } = notificationEvent

  // `default` still runs for a type the backend sends before this switch knows it.
  switch (type) {
    case NotificationType.BUILD_MIGRATION:
      return buildMigrationSummary(COUNT_NOUN_BY_TYPE[type]?.[status], notificationEvent.payload)
    case NotificationType.APP_VERSION_EXPORT:
    case NotificationType.BUILD_EXPORT:
      return buildExportSummary(notificationEvent, notificationEvent.payload)
    case NotificationType.APP_VERSION_IMPORT:
    case NotificationType.BUILD_IMPORT:
      return buildImportSummary(notificationEvent)
    default:
      return []
  }
}

/**
 * Every region the card puts on screen, lowercased and kept apart so a phrase cannot match across
 * two of them. Built from the derived summary rather than the raw payload, so the center searches
 * the text it rendered. The action's label is left out on purpose: it says what the card offers to
 * do rather than what happened, so it would match every notification of its type and status.
 */
const buildNotificationSearchFields = (
  notificationEvent: UserNotificationEvent,
  summary: NotificationSummary
): string[] =>
  [
    notificationEvent.title,
    notificationEvent.workspaceType,
    notificationEvent.workspaceName,
    notificationEvent.appName,
    notificationEvent.body,
    ...summary.map(({ text }) => text),
  ].flatMap((field) => (field ? [field.toLowerCase()] : []))
